import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import AdminDeleteModal from "../components/AdminDeleteModal";
import { useAuth } from "../contexts/AuthContext";
import { fmtMoney, fmtPct, fmtDate } from "../lib/format";

function ProductForm({ initial, customers, categories, onSubmit, onCancel }) {
    const [form, setForm] = useState({
        customer_id: initial?.customer_id || "",
        name: initial?.name || "",
        category_id: initial?.category_id || "",
        cost: initial?.cost ?? "0.0000",
        price: initial?.price ?? "0.0000",
        production_type: initial?.production_type || "in_house",
        allowed_overrun_pct: initial?.allowed_overrun_pct ?? "0.0000",
        allowed_underrun_pct: initial?.allowed_underrun_pct ?? "0.0000",
        active: initial?.active ?? true,
        notes: initial?.notes || "",
        description: initial?.description || "",
        blank_size: initial?.blank_size || "",
        blank_width: initial?.blank_width ?? "0.00000",
        blank_length: initial?.blank_length ?? "0.00000",
        box_style_id: initial?.box_style_id || "",
        joint_id: initial?.joint_id || "",
        test_id: initial?.test_id || "",
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [boxStyles, setBoxStyles] = useState([]);
    const [joints, setJoints] = useState([]);
    const [tests, setTests] = useState([]);

    // V1#3 — load Box dropdown options (active only).
    useEffect(() => {
        api.get("/product-options/box-styles", { params: { active: true } }).then((r) =>
            setBoxStyles(r.data),
        );
        api.get("/product-options/joints", { params: { active: true } }).then((r) =>
            setJoints(r.data),
        );
        api.get("/product-options/tests", { params: { active: true } }).then((r) =>
            setTests(r.data),
        );
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const selectedCategory = categories.find((c) => c.id === form.category_id);
    const isBoxes = selectedCategory?.name === "Boxes";

    // V1#5 — SQF preview computed live in JS for the UI; backend re-computes
    // the canonical value with high-precision Decimal.
    const blankSqfPreview = (() => {
        const w = parseFloat(form.blank_width);
        const l = parseFloat(form.blank_length);
        if (!isFinite(w) || !isFinite(l) || w <= 0 || l <= 0) return "0.0000";
        return ((w * l) / 144).toFixed(4);
    })();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.customer_id) return setError("Select a customer.");
        if (!form.name.trim()) return setError("Product name is required.");
        if (!form.category_id) return setError("Select a category (Boxes or Other).");
        setSubmitting(true);
        try {
            const payload = {
                customer_id: form.customer_id,
                name: form.name.trim(),
                category_id: form.category_id,
                cost: String(form.cost),
                price: String(form.price),
                production_type: form.production_type,
                allowed_overrun_pct: String(form.allowed_overrun_pct),
                allowed_underrun_pct: String(form.allowed_underrun_pct),
                active: form.active,
                notes: form.notes,
                description: form.description,
                blank_size: isBoxes ? form.blank_size : "",
                blank_width: isBoxes ? String(form.blank_width || "0") : "0",
                blank_length: isBoxes ? String(form.blank_length || "0") : "0",
                box_style_id: isBoxes ? form.box_style_id || null : null,
                joint_id: isBoxes ? form.joint_id || null : null,
                test_id: isBoxes ? form.test_id || null : null,
            };
            await onSubmit(payload);
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-3" data-testid="product-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Customer
                    </label>
                    <select
                        value={form.customer_id}
                        onChange={(e) => set("customer_id", e.target.value)}
                        disabled={!!initial}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white disabled:bg-gray-50"
                        data-testid="product-form-customer"
                        required
                    >
                        <option value="">— Select customer —</option>
                        {customers.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Product Name
                    </label>
                    <input
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        data-testid="product-form-name"
                        required
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Category <span className="text-red-600">*</span>
                    </label>
                    <select
                        value={form.category_id}
                        onChange={(e) => set("category_id", e.target.value)}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="product-form-category"
                        required
                    >
                        <option value="">— Select —</option>
                        {categories
                            .filter((c) => c.active !== false)
                            .map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                    </select>
                    <p className="text-[10px] text-gray-500 mt-1 italic">
                        Categories are locked to Boxes / Other.
                    </p>
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Active
                    </label>
                    <div className="flex items-end h-9">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                                id="prod-active"
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => set("active", e.target.checked)}
                                data-testid="product-form-active"
                            />
                            Active
                        </label>
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Description
                </label>
                <textarea
                    rows={2}
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    data-testid="product-form-description"
                    placeholder={
                        isBoxes
                            ? "e.g. 12x12x6 Single-Wall RSC, 32 ECT Kraft"
                            : "Free-form product description"
                    }
                />
            </div>

            {isBoxes && (
                <div
                    className="border border-gray-200 bg-gray-50 rounded-sm p-3 space-y-3"
                    data-testid="product-form-box-fields"
                >
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-700">
                        Box Specifications
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                Blank Width (in)
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                                value={form.blank_width}
                                onChange={(e) => set("blank_width", e.target.value)}
                                placeholder="0.00000"
                                data-testid="product-form-blank-width"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                Blank Length (in)
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                                value={form.blank_length}
                                onChange={(e) => set("blank_length", e.target.value)}
                                placeholder="0.00000"
                                data-testid="product-form-blank-length"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                SQF (auto)
                            </label>
                            <div
                                className="odr-input w-full rounded-sm border border-gray-200 px-3 text-sm odr-mono bg-gray-50 text-gray-800 flex items-center"
                                data-testid="product-form-blank-sqf"
                            >
                                {blankSqfPreview}
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1 italic">
                                W × L ÷ 144
                            </p>
                        </div>
                    </div>
                    <p className="text-[10px] text-gray-500 italic">
                        Up to 5 decimal places. Stored with high precision for quoting &amp;
                        costing.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                Box Style
                            </label>
                            <select
                                value={form.box_style_id}
                                onChange={(e) => set("box_style_id", e.target.value)}
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                data-testid="product-form-box-style"
                            >
                                <option value="">— None —</option>
                                {boxStyles.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                Joint
                            </label>
                            <select
                                value={form.joint_id}
                                onChange={(e) => set("joint_id", e.target.value)}
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                data-testid="product-form-joint"
                            >
                                <option value="">— None —</option>
                                {joints.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                Test
                            </label>
                            <select
                                value={form.test_id}
                                onChange={(e) => set("test_id", e.target.value)}
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                data-testid="product-form-test"
                            >
                                <option value="">— None —</option>
                                {tests.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <p className="text-[10px] text-gray-500 italic">
                        Manage Box Style / Joint / Test options in <strong>Settings</strong>.
                    </p>
                </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Cost
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                        value={form.cost}
                        onChange={(e) => set("cost", e.target.value)}
                        placeholder="0.0000"
                        data-testid="product-form-cost"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Price
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                        value={form.price}
                        onChange={(e) => set("price", e.target.value)}
                        placeholder="0.0000"
                        data-testid="product-form-price"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Overrun %
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                        value={form.allowed_overrun_pct}
                        onChange={(e) => set("allowed_overrun_pct", e.target.value)}
                        data-testid="product-form-overrun"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Underrun %
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                        value={form.allowed_underrun_pct}
                        onChange={(e) => set("allowed_underrun_pct", e.target.value)}
                        data-testid="product-form-underrun"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Internal Notes
                </label>
                <textarea
                    rows={2}
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    data-testid="product-form-notes"
                />
            </div>

            <p className="text-[11px] text-gray-500 italic">
                Cost/Price stored with 4-decimal precision. Display rounds to 2dp by default.
            </p>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                    {error}
                </div>
            )}

            <div className="flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="product-form-cancel"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                    data-testid="product-form-submit"
                >
                    {submitting ? "Saving…" : initial ? "Save Changes" : "Create Product"}
                </button>
            </div>
        </form>
    );
}

export default function Products() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [activeOnly, setActiveOnly] = useState(false);

    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const loadCustomers = useCallback(async () => {
        const { data } = await api.get("/customers", { params: { active: true } });
        setCustomers(data);
    }, []);

    const loadCategories = useCallback(async () => {
        // V1#3 — only the seeded protected categories (Boxes / Other) are user-facing
        const { data } = await api.get("/product-categories", { params: { locked: true } });
        setCategories(data);
    }, []);

    const loadProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search.trim()) params.q = search.trim();
            if (customerFilter) params.customer_id = customerFilter;
            if (activeOnly) params.active = true;
            const { data } = await api.get("/products", { params });
            setProducts(data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [search, customerFilter, activeOnly]);

    useEffect(() => {
        loadCustomers();
        loadCategories();
    }, [loadCustomers, loadCategories]);

    useEffect(() => {
        const t = setTimeout(loadProducts, 200);
        return () => clearTimeout(t);
    }, [loadProducts]);

    const customerOptions = useMemo(() => customers, [customers]);

    return (
        <div data-testid="products-page">
            <PageHeader
                title="Products"
                subtitle="Customer-scoped product master with tolerances and pricing"
                actions={
                    <button
                        onClick={() => setShowCreate(true)}
                        className="odr-btn inline-flex items-center gap-2 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4"
                        data-testid="products-add-button"
                    >
                        <Plus className="w-4 h-4" /> New Product
                    </button>
                }
            />

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-3 items-end">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search products…"
                        className="odr-input w-full rounded-sm border border-gray-300 pl-9 pr-3 text-sm"
                        data-testid="products-search"
                    />
                </div>
                <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white min-w-[180px]"
                    data-testid="products-customer-filter"
                >
                    <option value="">All customers</option>
                    {customerOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                        data-testid="products-active-only"
                    />
                    Active only
                </label>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="products-table">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th>Customer</th>
                            <th>Category</th>
                            <th className="text-right">Cost</th>
                            <th className="text-right">Price</th>
                            <th className="text-right">Over %</th>
                            <th className="text-right">Under %</th>
                            <th>Status</th>
                            <th>Updated</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={10} className="text-center text-gray-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && products.length === 0 && (
                            <tr>
                                <td colSpan={10} className="text-center text-gray-500">
                                    No products found.
                                </td>
                            </tr>
                        )}
                        {products.map((p) => (
                            <tr key={p.id} data-testid={`product-row-${p.id}`}>
                                <td className="font-medium">{p.name}</td>
                                <td className="text-sm text-gray-700">
                                    {p.customer_name_cached}
                                </td>
                                <td className="text-sm text-gray-700">
                                    {p.category_name_cached || "—"}
                                </td>
                                <td className="text-right odr-mono">{fmtMoney(p.cost)}</td>
                                <td className="text-right odr-mono">{fmtMoney(p.price)}</td>
                                <td className="text-right odr-mono">{fmtPct(p.allowed_overrun_pct)}</td>
                                <td className="text-right odr-mono">{fmtPct(p.allowed_underrun_pct)}</td>
                                <td>
                                    {p.active ? (
                                        <span className="odr-badge odr-badge-open">Active</span>
                                    ) : (
                                        <span className="odr-badge odr-badge-neutral">Inactive</span>
                                    )}
                                </td>
                                <td className="text-xs text-gray-500 whitespace-nowrap">
                                    {fmtDate(p.updated_at)}
                                </td>
                                <td className="text-right">
                                    <button
                                        onClick={() => setEditing(p)}
                                        className="text-sm text-red-700 hover:text-red-900 font-semibold"
                                        data-testid={`edit-product-${p.id}`}
                                    >
                                        Edit
                                    </button>
                                    {isAdmin && (
                                        <button
                                            onClick={() => setDeleting(p)}
                                            className="ml-3 text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                            data-testid={`delete-product-${p.id}`}
                                            title="Hard-delete (admin)"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showCreate && (
                <Modal title="New Product" size="lg" onClose={() => setShowCreate(false)}>
                    <ProductForm
                        customers={customers}
                        categories={categories}
                        onCancel={() => setShowCreate(false)}
                        onSubmit={async (payload) => {
                            await api.post("/products", payload);
                            setShowCreate(false);
                            await loadProducts();
                        }}
                    />
                </Modal>
            )}

            {editing && (
                <Modal
                    title={`Edit ${editing.name}`}
                    size="lg"
                    onClose={() => setEditing(null)}
                >
                    <ProductForm
                        initial={editing}
                        customers={customers}
                        categories={categories}
                        onCancel={() => setEditing(null)}
                        onSubmit={async (payload) => {
                            const { customer_id, ...patch } = payload;
                            await api.patch(`/products/${editing.id}`, patch);
                            setEditing(null);
                            await loadProducts();
                        }}
                    />
                </Modal>
            )}

            <AdminDeleteModal
                open={!!deleting}
                module="products"
                recordId={deleting?.id}
                endpoint={deleting ? `/products/${deleting.id}` : ""}
                label={deleting ? `product '${deleting.name}'` : ""}
                onClose={() => setDeleting(null)}
                onDeleted={() => loadProducts()}
                testIdPrefix="delete-product"
            />
        </div>
    );
}
